<template>
  <div class="hero-banner-wrap" :class="{ 'h5-hero-banner-wrap': isH5 }">
    <div class="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Banner Container -->
      <div class="hero-banner-main relative overflow-hidden shadow-2xl group bg-slate-900 ring-1 ring-slate-900/5 select-none transition-all duration-500">
        <!-- Dynamic Background Image Layer -->
        <div
          v-for="(item, index) in bannerList"
          :key="item.id"
          class="absolute inset-0 transition-opacity duration-700 ease-in-out"
          :class="index === currentSlide ? 'opacity-100 z-0' : 'opacity-0 -z-10'"
        >
          <img
            :src="getBannerImgUrl(item.img_url)"
            :alt="'banner-' + index"
            class="w-full h-full object-cover"
          />
          <!-- Content mode: left gradient for text readability -->
          <div v-if="hasCourseOverlay(item)" class="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-transparent"></div>
          <!-- Poster mode: minimal bottom gradient -->
          <div v-else class="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent"></div>
        </div>

        <!-- Content Overlay Layer (left aligned - when banner has course_info) -->
        <div
          v-if="currentBanner && hasCourseOverlay(currentBanner)"
          class="absolute inset-0 z-10 flex flex-col justify-center px-6 md:px-20 max-w-4xl"
        >
          <div :class="['transition-all duration-500 transform', isAnimating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0']">
            <!-- Badge / Tag -->
            <div class="flex items-center gap-3 mb-4 md:mb-6">
              <span class="bg-brand-500 text-white text-[10px] md:text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-lg shadow-brand-500/30 tracking-wide uppercase">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41Z"/></svg>
                {{ currentBanner.course_info.tag || '精品推荐' }}
              </span>
              <div class="h-4 w-px bg-white/20"></div>
              <span class="text-brand-200 text-xs md:text-sm font-bold tracking-wide">
                {{ currentBanner.course_info.institution || '' }}
              </span>
            </div>

            <!-- Main Title -->
            <h1 class="text-2xl md:text-4xl lg:text-5xl font-black text-white leading-tight mb-4 md:mb-6 tracking-tight" style="display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
              {{ currentBanner.course_info.title }}
            </h1>

            <!-- Meta Info Row -->
            <div class="flex flex-wrap items-center gap-4 md:gap-6 text-slate-300 mb-6 md:mb-8 font-medium text-xs md:text-sm">
              <div v-if="currentBanner.course_info.instructor" class="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full border border-white/5 backdrop-blur-sm">
                <div class="w-5 h-5 rounded-full bg-white/20 p-0.5 overflow-hidden">
                  <img :src="getAvatarUrl(currentBanner.course_info.instructor)" class="w-full h-full rounded-full" />
                </div>
                <span class="text-white">{{ currentBanner.course_info.instructor }}</span>
              </div>
              <div v-if="currentBanner.course_info.studentCount" class="flex items-center gap-1.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <span>{{ currentBanner.course_info.studentCount }} 人在学</span>
              </div>
            </div>

            <!-- Action Area: CTA + Price -->
            <div class="flex items-center gap-6 md:gap-8">
              <button
                class="group/btn h-10 md:h-14 px-8 md:px-10 rounded-full bg-white text-slate-900 font-bold text-sm md:text-base flex items-center gap-2 transition-all hover:bg-brand-50 hover:text-brand-600 hover:scale-105 active:scale-95 shadow-xl border-none cursor-pointer"
                @click="jumpUrl(currentBanner)"
              >
                开始学习
                <svg class="transition-transform duration-200 group-hover/btn:translate-x-1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
              </button>

              <div class="flex flex-col">
                <div class="flex items-baseline gap-1.5">
                  <span v-if="currentBanner.course_info.price === 0" class="text-2xl md:text-3xl font-black text-brand-400">免费</span>
                  <template v-else>
                    <span class="text-xs md:text-sm font-bold text-white/60">¥</span>
                    <span class="text-2xl md:text-3xl font-black text-white tracking-tighter">{{ currentBanner.course_info.price }}</span>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Poster mode CTA (when no course_info) -->
        <div
          v-if="currentBanner && !hasCourseOverlay(currentBanner) && currentBanner.href_type !== 1"
          class="absolute bottom-8 left-8 md:left-12 z-20 transition-all duration-500"
          :class="isAnimating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'"
        >
          <button
            class="group flex items-center gap-2 h-10 md:h-14 px-6 md:px-8 rounded-full bg-white/90 backdrop-blur-xl text-slate-900 font-bold text-sm md:text-base border-none cursor-pointer transition-all duration-200 shadow-md hover:bg-brand-500 hover:text-white hover:shadow-[0_4px_20px_rgba(16,185,129,0.3)]"
            @click="jumpUrl(currentBanner)"
          >
            查看详情
            <svg class="transition-transform duration-200 group-hover:translate-x-1" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
          </button>
        </div>

        <!-- Indicators + Arrows -->
        <!-- Indicators (position adjusts based on content mode) -->
        <div
          v-if="bannerList.length > 1"
          :class="['absolute bottom-6 flex gap-1.5 z-20 transition-all duration-500',
            currentBanner && hasCourseOverlay(currentBanner)
              ? 'left-1/2 -translate-x-1/2 md:translate-x-0 md:left-20'
              : 'left-1/2 -translate-x-1/2 md:translate-x-0 md:left-auto md:right-40'
          ]"
        >
          <button
            v-for="(item, idx) in bannerList"
            :key="'dot-' + idx"
            @click="goToSlide(idx)"
            class="h-1 rounded-full border-none cursor-pointer transition-all duration-300 p-0"
            :class="idx === currentSlide ? 'bg-brand-500 w-8 shadow-[0_1px_4px_rgba(16,185,129,0.3)]' : 'bg-white/40 w-2 hover:bg-white/80'"
          ></button>
        </div>

        <div class="absolute bottom-8 right-8 flex items-center gap-4 z-20" v-if="bannerList.length > 1">
          <!-- Arrows -->
          <div class="hidden md:flex gap-3" v-if="!isH5">
            <button @click="handlePrev" class="w-10 h-10 rounded-full border border-white/10 bg-black/20 backdrop-blur-xl text-white flex items-center justify-center cursor-pointer transition-all duration-200 hover:bg-white hover:text-slate-900 hover:scale-110 active:scale-95">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            </button>
            <button @click="handleNext" class="w-10 h-10 rounded-full border border-white/10 bg-black/20 backdrop-blur-xl text-white flex items-center justify-center cursor-pointer transition-all duration-200 hover:bg-white hover:text-slate-900 hover:scale-110 active:scale-95">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Dock -->
    <div class="absolute bottom-0 left-0 right-0 z-30 px-4 translate-y-1/2 pointer-events-none" v-if="!isH5">
      <div class="max-w-3xl mx-auto pointer-events-auto">
        <div class="bg-white/[0.92] dark:bg-slate-900/[0.92] backdrop-blur-[20px] rounded-2xl p-2 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.2)] dark:shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] border border-white/50 dark:border-slate-700/50 flex items-center gap-2 ring-1 ring-white/40 dark:ring-white/5 transition-colors duration-300">
          <div class="hero-search-dock__input-wrap flex-1 flex items-center h-12 md:h-14 bg-slate-50 dark:bg-slate-900/50 rounded-xl px-4 border-2 border-transparent transition-all duration-200 focus-within:bg-white dark:focus-within:bg-slate-900 focus-within:border-brand-200 dark:focus-within:border-brand-900 focus-within:ring-2 focus-within:ring-brand-50 dark:focus-within:ring-brand-900/20">
            <svg class="hero-search-dock__search-icon text-slate-400 mr-3 flex-shrink-0 transition-colors duration-200" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            <input
              type="text"
              v-model="searchValue"
              @keydown.enter="handleSearchSubmit"
              placeholder="搜索你感兴趣的课程、导师..."
              class="flex-1 bg-transparent outline-none border-none text-slate-900 dark:text-slate-100 font-semibold text-sm md:text-base h-full w-full placeholder-slate-400/80 dark:placeholder-slate-500"
            />
          </div>
          <button @click="handleSearchSubmit" class="h-12 md:h-14 px-6 md:px-8 bg-slate-900 dark:bg-brand-600 text-white rounded-xl font-bold text-sm md:text-base border-none cursor-pointer transition-all duration-200 shadow-md flex-shrink-0 hover:bg-brand-600 dark:hover:bg-brand-500 hover:shadow-lg active:scale-95">
            搜索
          </button>
        </div>
        <!-- Tags -->
        <div class="flex justify-center mt-3 gap-2 sm:gap-4 flex-wrap">
          <button
            v-for="tag in hotSearchTags"
            :key="tag"
            @click="handleTagClick(tag)"
            class="text-xs font-medium text-slate-400 dark:text-slate-500 bg-white/60 dark:bg-slate-900/60 backdrop-blur border border-white/20 dark:border-white/5 py-1 px-2.5 rounded-lg cursor-pointer transition-all duration-200 shadow-sm hover:text-brand-600 dark:hover:text-brand-400 hover:bg-white dark:hover:bg-slate-800"
          >
            #{{ tag }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isH5, isMobile } from "@saas/components/src";

export default {
  name: "HeroBanner",
  props: {
    bannerList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentSlide: 0,
      isAnimating: false,
      searchValue: "",
      isH5: isH5(),
      isElectron: false,
      isMobile: isMobile(),
      timer: null,
      animationTimer: null,
      hotSearchTags: ["Java", "Python", "考研", "雅思", "公务员"],
    };
  },
  computed: {
    currentBanner() {
      return this.bannerList[this.currentSlide] || null;
    },
  },
  mounted() {
    this.isElectron = window.isElectron;
    this.startAutoPlay();
  },
  beforeDestroy() {
    this.stopAutoPlay();
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
      this.animationTimer = null;
    }
  },
  methods: {
    getBannerImgUrl(url) {
      if (!url) return "";
      // 本地 webpack 打包的图片（data: 或 /img/ 开头）不追加 CDN 参数
      if (url.startsWith("data:") || url.startsWith("/img/") || url.startsWith("blob:")) {
        return url;
      }
      return url + "?imageView2/0/w/1200";
    },
    startAutoPlay() {
      if (this.bannerList.length <= 1) return;
      this.timer = setInterval(() => {
        this.handleNext();
      }, 6000);
    },
    stopAutoPlay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    handleNext() {
      if (this.bannerList.length <= 1) return;
      this.stopAutoPlay();
      if (this.animationTimer) {
        clearTimeout(this.animationTimer);
      }
      this.isAnimating = true;
      this.animationTimer = setTimeout(() => {
        this.animationTimer = null;
        this.currentSlide = (this.currentSlide + 1) % this.bannerList.length;
        this.isAnimating = false;
        this.startAutoPlay();
      }, 500);
    },
    handlePrev() {
      if (this.bannerList.length <= 1) return;
      this.stopAutoPlay();
      if (this.animationTimer) {
        clearTimeout(this.animationTimer);
      }
      this.isAnimating = true;
      this.animationTimer = setTimeout(() => {
        this.animationTimer = null;
        this.currentSlide =
          (this.currentSlide - 1 + this.bannerList.length) %
          this.bannerList.length;
        this.isAnimating = false;
        this.startAutoPlay();
      }, 500);
    },
    goToSlide(idx) {
      this.stopAutoPlay();
      this.currentSlide = idx;
      this.startAutoPlay();
    },
    jumpUrl(item) {
      if (!item) return;
      if (item.href_type === 2) {
        if (window.isElectron) {
          window.openUrlByExternal(item.href);
        } else {
          window.open(item.href);
        }
      } else if (item.href_type === 3) {
        let url = `/details/${item.id}`;
        if (window.isElectron || this.isMobile) {
          this.$router.push(url);
        } else {
          window.open(url);
        }
      }
    },
    handleSearchSubmit() {
      if (!this.searchValue.trim()) return;
      let url = `/search-result?keyword=${this.searchValue}`;
      if (window.isElectron || this.isMobile) {
        this.$router.push(url);
      } else {
        window.open(url);
      }
    },
    handleTagClick(tag) {
      this.searchValue = tag;
      this.handleSearchSubmit();
    },
    hasCourseOverlay(item) {
      return item && item.course_info && item.course_info.title;
    },
    getAvatarUrl(name) {
      return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(name)}`;
    },
  },
  watch: {
    bannerList(newVal) {
      if (newVal.length > 0) {
        this.currentSlide = 0;
        this.stopAutoPlay();
        this.startAutoPlay();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.hero-banner-wrap {
  position: relative;
  padding-bottom: 64px;
  padding-top: 16px;

  .hero-search-dock__input-wrap:focus-within .hero-search-dock__search-icon {
    color: #10b981;
  }
}

// Banner 主体 — 按图片原始比例 1456x500
.hero-banner-main {
  aspect-ratio: 1456 / 500;
  max-height: var(--banner-height, 500px);
  border-radius: var(--banner-radius, 2.5rem);
}

// h5 适配
.hero-banner-wrap.h5-hero-banner-wrap {
  padding: 12px 12px 0;

  // max-w-[1520px] container — reset padding
  > div {
    padding: 0 !important;
    max-width: 100% !important;
  }

  // Banner container itself — fix height and border-radius
  > div > div:first-child {
    height: 160px !important;
    aspect-ratio: auto !important;
    border-radius: 16px !important;
  }

  // Content overlay (course info) — fix padding and text sizes
  .absolute.inset-0.z-10 {
    padding: 16px !important;
    max-width: 100% !important;

    // Badge / Tag row
    .mb-4, .mb-6 {
      margin-bottom: 8px !important;
    }
    span.bg-brand-500 {
      font-size: 9px !important;
      padding: 2px 8px !important;
    }
    span.text-brand-200 {
      font-size: 10px !important;
    }

    // Main Title
    h1 {
      font-size: 16px !important;
      line-height: 1.3 !important;
      margin-bottom: 8px !important;
    }

    // Meta Info Row
    .text-slate-300 {
      font-size: 10px !important;
      gap: 6px !important;
      margin-bottom: 12px !important;
      .bg-white\/10 {
        padding: 2px 6px !important;
      }
    }

    // CTA Button ("开始学习")
    button {
      height: 32px !important;
      padding: 0 16px !important;
      font-size: 12px !important;
      border-radius: 16px !important;
      svg {
        width: 14px !important;
        height: 14px !important;
      }
    }

    // Price text
    .text-2xl, .text-3xl {
      font-size: 18px !important;
    }
    .text-xs.font-bold {
      font-size: 10px !important;
    }
  }

  // Poster mode CTA ("查看详情") — fix position + button size
  > div > div:first-child > .absolute.bottom-8 {
    bottom: 10px !important;
    left: 12px !important;
    button {
      height: 28px !important;
      padding: 0 14px !important;
      font-size: 11px !important;
      border-radius: 14px !important;
      gap: 4px !important;
      svg {
        width: 12px !important;
        height: 12px !important;
      }
    }
  }

  // Indicators — fix position and sizing
  .absolute.bottom-6 {
    bottom: 8px !important;
    gap: 3px !important;
    button {
      height: 3px !important;
    }
    button.w-8 {
      width: 16px !important;
    }
    button.w-2 {
      width: 4px !important;
    }
  }

  // Arrow buttons — hide on mobile
  .absolute.bottom-8.right-8 {
    display: none !important;
  }
}
</style>
